import { useState, useEffect, Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink, useLocation } from "react-router-dom";
import {
  faFileContract,
  faTableTree,
  faTag,
  faChartSine,
  faRectangleHistoryCircleUser,
  faChevronRight,
  faChevronDown,
  faUsers,
  faIdCard,
  faThumbTack,
  faThumbTackSlash,
  faBrowsers,
  faLaptop,
  faBoxOpen
} from "@fortawesome/pro-regular-svg-icons";
import { useAuth } from "../providers/AuthContext"; // Import useAuth
import { useSidebar } from "../providers/SidebarContext";

export default function Sidebar() {
  const { isSidebarOpen, setIsSidebarOpen, tenants, agreements, isSticky, setIsSticky, scoFormSettings } = useSidebar(); // Sidebar state
  const { isRole } = useAuth(); // Destructure getAccessToken


  const handleMouseEnter = () => setIsSidebarOpen(true); // Öppna sidebar vid hover

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('aside') === null) {
        setIsSidebarOpen(false);
      }
    };

    if(isSticky) {
      document.removeEventListener('click', handleClickOutside);

    } else {
      document.addEventListener('click', handleClickOutside);
      setIsSidebarOpen(false);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isSticky]);

  return (
    <>
      <aside
        onMouseEnter={handleMouseEnter} // Aktivera hover
        className={`left-0 bg-white border-r border-mute-200 transition-all duration-100 h-[100vh] z-10 ${isSidebarOpen ? "w-[20rem]" : "w-[4rem]"
          } md:block `}
        style={{
          pointerEvents: isSidebarOpen ? "auto" : "initial", // Anpassa för utfällt läge
        }}
      >
        <nav className="flex flex-col h-full pt-6">
          <div className="relative">
            {isSidebarOpen && <button className="absolute right-2 -top-6" onClick={() => setIsSticky(!isSticky)}>
              {isSticky ? 
              <FontAwesomeIcon icon={faThumbTackSlash} className="w-3 h-3" />
              :
               <FontAwesomeIcon icon={faThumbTack} className="w-3 h-3" />}
             
            </button>}
         
          </div>
          <ul className="flex-1">
            {agreements?.map((agreement) => {
              return (
                <div key={agreement.agreementPublicId}>
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faFileContract} />}
                    text={agreement.name}
                    to={`/agreements/${agreement.agreementPublicId}/edit`}
                    isSidebarOpen={isSidebarOpen}
                  />
                  {isRole("poweruser") && (
                    <>
                      {tenants.length > 0 && (
                        tenants.map((tenant) => (
                          <Fragment key={tenant.tenantPublicId}>
                            <SidebarItem
                              key={tenant.id}
                              icon={<FontAwesomeIcon icon={faTableTree} />}
                              level={2}
                              text={tenant.displayName}
                              disabled={true}
                              to={`/${agreement.agreementPublicId}/tenants/${tenant.tenantPublicId}`}
                              isSidebarOpen={isSidebarOpen}
                            />
                            <SidebarItem
                              icon={<FontAwesomeIcon icon={faUsers} />}
                              level={3}
                              text="Users"
                              to={`/${agreement.agreementPublicId}/${tenant.tenantPublicId}/users`}
                              isSidebarOpen={isSidebarOpen}
                            />
                            <SidebarItem
                              icon={<FontAwesomeIcon icon={faIdCard} />}
                              level={3}
                              text="Licenses"
                              to={`/${agreement.agreementPublicId}/${tenant.tenantPublicId}/licenses`}
                              isSidebarOpen={isSidebarOpen}
                            />
                            {scoFormSettings?.supportsStandaloneHardwareOrder && agreement.scoFormsEnabled && (
                              <SidebarItem
                                icon={<FontAwesomeIcon icon={faBoxOpen} />}
                                level={3}
                                text="Order hardware"
                                to={`/${agreement.agreementPublicId}/${tenant.tenantPublicId}/hardware-order`}
                                isSidebarOpen={isSidebarOpen}
                              />
                            )}
                          </Fragment>
                        ))
                      )}
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faTag} />}
                        level={2}
                        text="Add-on services"
                        to={`/${agreement.agreementPublicId}/add-on-services`}
                        isSidebarOpen={isSidebarOpen}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faBrowsers} />}
                        level={2}
                        text="Domains"
                        to={`/${agreement.agreementPublicId}/domains`}
                        isSidebarOpen={isSidebarOpen}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faLaptop} />}
                        level={2}
                        text="Device inventory"
                        to={`/${agreement.agreementPublicId}/device-inventory`}
                        isSidebarOpen={isSidebarOpen}
                      />
                      <SidebarItem
                        level={2}
                        icon={<FontAwesomeIcon icon={faChartSine} />}
                        text="Index adjustments"
                        to={`/${agreement.agreementPublicId}/indexadjustments`}
                        isSidebarOpen={isSidebarOpen}
                      />
                      <SidebarItem
                        level={2}
                        icon={<FontAwesomeIcon icon={faRectangleHistoryCircleUser} />}
                        text="History & billing"
                        to={`/${agreement.agreementPublicId}/history-billing`}
                        isSidebarOpen={isSidebarOpen}
                      />
                    </>
                  )}
                </div>)
            })}

          </ul>
        </nav>
      </aside>
    </>
  );
}

function SidebarItem({ icon, text, children, to, isSidebarOpen, level = 1, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const toggleSubmenu = (e) => {
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  const isActive = children?.some(child => location.pathname.startsWith(child.to)) || location.pathname === to || location.pathname.startsWith(`${to}/`) || (to === "/agreements" && location.pathname.startsWith("/agreement/"));

  return (
    <>
      <NavLink
        to={disabled ? "#" : to}
        className={`flex items-center py-3 px-6 transition-all duration-200 truncate group hover:bg-primary-300 ${disabled && 'pointer-events-none'} ${isSidebarOpen ? "justify-between" : "justify-center"
          } ${isActive ? "bg-primary-100" : ""} ${isActive && level === 3 ? "bg-primary-200" : ""} ${level === 3 && isSidebarOpen && "pl-16"} ${level === 2 && isSidebarOpen && "pl-12"}`}
      /* onClick={children?.length > 0 && toggleSubmenu}  */
      >
        <li className="flex items-center">
          <span>{icon}</span>
          {isSidebarOpen && (
            <span
              className={`ml-4 transition-opacity duration-200 truncate w-[14rem] ${isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
            >
              {text}
            </span>
          )}
          {children?.length > 0 && isSidebarOpen && (
            <FontAwesomeIcon
              icon={!isOpen ? faChevronRight : faChevronDown}
              className="ml-4 cursor-pointer"
              onClick={toggleSubmenu} // Only toggle submenu through chevron icon
            />
          )}
        </li>
      </NavLink>
      {children?.length > 0 && (
        <div className={`overflow-hidden transition-[max-height] duration-100 ${isSidebarOpen && isOpen ? "max-h-40" : "max-h-0"}`}>
          <ul className="ml-8">
            {children.map((child) => (
              <li key={child.to}>
                <NavLink
                  to={child.to}
                  className="ml-4 block py-2 px-3 hover:bg-primary-200"
                >
                  {child.text}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

